/**
 * Professional role catalog for intent-first signup.
 *
 * Maps the product taxonomy onto the backend `ActivityType` enum (no new enum
 * values). The same activity type can apply to an individual or a company; the
 * available set differs by accountKind (the documented asymmetry):
 *   - individual: Broker, Consultant, Analyst, Appraiser, Investor
 *   - company:    Brokerage, Consulting, Analysis, Property-Mgmt, Facilities-Mgmt, Development
 *
 * Each professional role needs a FAAL licence (verified post-signup) except
 * Investor, where the licence is optional.
 */

export type ActivityType =
  | 'BROKER'
  | 'CONSULTANT'
  | 'ANALYST'
  | 'APPRAISER'
  | 'INVESTOR'
  | 'PROPERTY_MANAGEMENT'
  | 'FACILITIES_MANAGEMENT'
  | 'DEVELOPER';

export type AccountKind = 'individual' | 'company';

export interface RoleDef {
  type: ActivityType;
  icon: string; // remixicon class
  labelEn: string;
  labelAr: string;
  /** Whether a FAAL licence is required to verify this role. */
  requiresLicense: boolean;
}

const ROLE_DEFS: Record<ActivityType, Omit<RoleDef, 'type'> & { individualEn?: string; individualAr?: string }> = {
  BROKER: {
    icon: 'ri-key-2-line',
    labelEn: 'Brokerage & Marketing Company',
    labelAr: 'شركة وساطة وتسويق عقاري',
    individualEn: 'Real Estate Broker & Marketer',
    individualAr: 'مسوّق ووسيط عقاري',
    requiresLicense: true,
  },
  CONSULTANT: {
    icon: 'ri-customer-service-2-line',
    labelEn: 'Real Estate Consulting Company',
    labelAr: 'شركة استشارات عقارية',
    individualEn: 'Real Estate Consultant',
    individualAr: 'مستشار عقاري',
    requiresLicense: true,
  },
  ANALYST: {
    icon: 'ri-line-chart-line',
    labelEn: 'Real Estate Analysis Company',
    labelAr: 'شركة تحليل عقاري',
    individualEn: 'Real Estate Analyst',
    individualAr: 'محلل عقاري',
    requiresLicense: true,
  },
  APPRAISER: {
    icon: 'ri-scales-3-line',
    labelEn: 'Real Estate Appraiser',
    labelAr: 'مقيّم عقاري',
    requiresLicense: true,
  },
  INVESTOR: {
    icon: 'ri-funds-line',
    labelEn: 'Investor',
    labelAr: 'مستثمر',
    requiresLicense: false,
  },
  PROPERTY_MANAGEMENT: {
    icon: 'ri-building-2-line',
    labelEn: 'Property Management Company',
    labelAr: 'شركة إدارة أملاك',
    requiresLicense: true,
  },
  FACILITIES_MANAGEMENT: {
    icon: 'ri-tools-line',
    labelEn: 'Facilities Management Company',
    labelAr: 'شركة إدارة مرافق',
    requiresLicense: true,
  },
  DEVELOPER: {
    icon: 'ri-building-4-line',
    labelEn: 'Real Estate Development Company',
    labelAr: 'شركة تطوير عقاري',
    requiresLicense: true,
  },
};

const INDIVIDUAL_TYPES: ActivityType[] = ['BROKER', 'CONSULTANT', 'ANALYST', 'APPRAISER', 'INVESTOR'];
const COMPANY_TYPES: ActivityType[] = ['BROKER', 'CONSULTANT', 'ANALYST', 'PROPERTY_MANAGEMENT', 'FACILITIES_MANAGEMENT', 'DEVELOPER'];

export function rolesForKind(kind: AccountKind): RoleDef[] {
  const types = kind === 'company' ? COMPANY_TYPES : INDIVIDUAL_TYPES;
  return types.map((type) => {
    const d = ROLE_DEFS[type];
    const isIndividual = kind === 'individual';
    return {
      type,
      icon: d.icon,
      requiresLicense: d.requiresLicense,
      labelEn: isIndividual && d.individualEn ? d.individualEn : d.labelEn,
      labelAr: isIndividual && d.individualAr ? d.individualAr : d.labelAr,
    };
  });
}

export function roleLabel(type: ActivityType, kind: AccountKind, isAr: boolean): string {
  const d = ROLE_DEFS[type];
  const isIndividual = kind === 'individual';
  if (isAr) return isIndividual && d.individualAr ? d.individualAr : d.labelAr;
  return isIndividual && d.individualEn ? d.individualEn : d.labelEn;
}

export function roleRequiresLicense(type: ActivityType): boolean {
  return ROLE_DEFS[type]?.requiresLicense ?? true;
}
